import { SELECTOR } from '../../client/Arkose';
import { Environment } from '../utils/environment';

const removeScript = (): void => {
	const currentScript = document.getElementById(SCRIPT_ID);
	if (currentScript) {
		currentScript.remove();
	}
};

const isProduction = (environment: Environment): boolean => environment === Environment.Production;

type Mode = 'inline' | 'lightbox';

type Config = {
	selector: string;
	mode: Mode;
	onReady?: () => void;
	onShown?: () => void;
	onShow?: () => void;
	onSuppress?: () => void;
	onCompleted?: (response: { token: string }) => void;
	onReset?: () => void;
	onHide?: () => void;
	onError?: (response: { error: { error?: string } }) => void;
	onFailed?: (response: unknown) => void;
};

type Enforcement = {
	run: () => void;
	reset: () => void;
	setConfig: (config: Config) => void;
};

declare global {
	interface Window {
		setupDetect?: (enforcement: Enforcement) => void;
	}
}

const SCRIPT_ID = 'detect-arkose-sdk';
const SETUP_FUNCTION_NAME = 'setupDetect';

const determinePublicKey = (environment: Environment): string => {
	if (isProduction(environment)) {
		return 'F74462F6-01AA-4323-99D7-4F53118BF5C8';
	} else {
		return '0ECC6D89-EF01-47E3-BAE3-303BBF2A3C4C';
	}
};

const addScript = (environment: Environment): HTMLElement => {
	const publicKey = determinePublicKey(environment);
	const arkoseSdk = `https://arkose-client.chime.com/v2/${publicKey}/api.js`;
	removeScript();
	const script = document.createElement('script');
	script.id = SCRIPT_ID;
	script.type = 'text/javascript';
	script.src = arkoseSdk;
	script.setAttribute('data-callback', SETUP_FUNCTION_NAME);
	script.setAttribute('data-testid', SCRIPT_ID);
	script.async = true;
	script.defer = true;
	document.body.appendChild(script);
	return script;
};

interface SetupEnforcementParams {
	onCompleted: OnCompleted;
	onReady: OnReady;
	onError: OnError;
}

const setupEnforcement = (params: SetupEnforcementParams) => (enforcement: Enforcement) => {
	enforcement.setConfig({
		selector: `#${SELECTOR}`,
		mode: 'inline',
		onReady: params.onReady,
		onCompleted: response => params.onCompleted(response.token),
		onError: () => params.onError,
	});
};

export const teardownArkose = (): void => {
	if (window[SETUP_FUNCTION_NAME]) {
		delete window[SETUP_FUNCTION_NAME];
	}

	removeScript();
};

export type OnCompleted = (sessionToken: string) => void;
export type OnReady = () => void;
export type OnError = () => void;

export interface SetupArkoseParams {
	environment: Environment;
	onCompleted: OnCompleted;
	onReady: OnReady;
	onError: OnError;
}

export const setupArkose = (params: SetupArkoseParams): void => {
	const { environment, onCompleted, onError, onReady } = params;

	window[SETUP_FUNCTION_NAME] = setupEnforcement({ onCompleted, onError, onReady });
	const scriptElement = addScript(environment);

	scriptElement.onerror = onError;
};
